"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Award, HardHat, Users, CircleCheck as CheckCircle2, Clock3, HeartHandshake, MessageSquare, ArrowRight, MapPin, Building2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function TeamImage() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="w-full aspect-[4/3] bg-muted animate-pulse" />;
  }
  const matchedPhoto = posts.find(post => {
    const fields = post.customFields || (post as any).acf;
    return fields?.photo_name === "About Team Image";
  });
  const fields = matchedPhoto?.customFields || (matchedPhoto as any)?.acf;
  const src = fields?.image || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Mansion-Pic.png";
  const alt = fields?.alt_text || "BMF Scaffolding structure on a large mansion estate refurbishment";
  const isStatic = typeof src === "string" && src.startsWith("/img/");
  return <img src={src} data-wvc-srcset={isStatic ? `${src}-wvc-srcset` : ""} data-wvc-sizes={isStatic ? `${src}-wvc-sizes` : ""} alt={alt} loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover" />;
}
function ValuesContent() {
  const shouldReduceMotion = useReducedMotion();
  const valuesList = [{
    id: "val-1",
    icon: ShieldCheck,
    title: "Safety first",
    description: "BMF's non-negotiable standard on every site, backed by NASC and CHAS accreditation."
  }, {
    id: "val-2",
    icon: Clock3,
    title: "Reliability",
    description: "Turning up when promised and delivering scaffolding that's ready on schedule."
  }, {
    id: "val-3",
    icon: MessageSquare,
    title: "Clear communication",
    description: "Direct contact with the team managing your job, start to finish."
  }, {
    id: "val-4",
    icon: HardHat,
    title: "Personal accountability",
    description: "Director-led oversight — you always know who is responsible for your site."
  }, {
    id: "val-5",
    icon: HeartHandshake,
    title: "Long-term relationships",
    description: "Built on trust with contractors, developers and homeowners who come back project after project."
  }];
  const workforceHighlights = [{
    id: "wf-1",
    title: "Qualified scaffolding operatives",
    detail: "Our scaffolders hold recognised industry qualifications."
  }, {
    id: "wf-2",
    title: "Continuous CITB training",
    detail: "Regular refresher courses in working at height, manual handling, and rescue plans."
  }, {
    id: "wf-3",
    title: "Verified accreditations",
    detail: "Certified standards under NASC and CHAS schemes."
  }, {
    id: "wf-4",
    title: "Full PPE standard",
    detail: "Site-ready crews equipped with harnesses, hard hats, and high-visibility gear."
  }];
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        delay: shouldReduceMotion ? 0 : i * 0.075,
        ease: [0.16, 1, 0.3, 1]
      }
    })
  };
  return <section id="our-team-values" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Top Eyebrow and Heading */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="w-6 h-[3px] bg-primary block" />
            <span className="text-xs md:text-sm font-bold tracking-wider uppercase text-primary">
              Our workforce and principles
            </span>
          </div>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Built on standards, driven by safety
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            BMF Scaffolding Ltd combines the muscle and engineering discipline of a tier-one contractor with the direct care and responsiveness of a family-run business. Guided by Pete Wood's 20+ years of frontline expertise, we honour Adam Story's founding legacy across every structure we erect.
          </p>
        </div>

        {/* 2-Column Overview: Photo/Capability + Values Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Team Capability Block & Imagery */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="relative rounded-lg overflow-hidden border border-border bg-card shadow-sm">
              <TeamImage />
              <div className="p-6 bg-card text-card-foreground border-t border-border">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-md bg-accent text-accent-foreground">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-default text-base font-bold text-foreground">
                      Qualified frontline personnel
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Serving Berkshire, Surrey, Hampshire & London
                    </p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                  Every scaffolder representing BMF is vetted, trained, and accountable. We invest in ongoing professional development so our clients receive reliable access solutions built to current British standards.
                </p>
              </div>
            </div>

            {/* Workforce Credentials Checklist */}
            <div className="bg-muted/40 rounded-lg p-6 border border-border">
              <h4 className="font-default text-sm font-bold uppercase tracking-wider text-foreground mb-4">
                Workforce standards at a glance
              </h4>
              <div className="space-y-3">
                {workforceHighlights.map((wf, idx) => <div key={wf.id} data-index={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <span className="text-sm font-semibold text-foreground block">
                        {wf.title}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {wf.detail}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Right Column: 5 Corporate Values */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {valuesList.map((val, index) => {
              const IconComponent = val.icon;
              return <motion.div key={val.id} data-index={index} custom={index} initial="hidden" whileInView="show" viewport={{
                once: true,
                amount: 0.2
              }} variants={cardVariants}>
                    <Card className="h-full border border-border shadow-xs hover:border-primary/50 transition-[border-color,box-shadow] duration-200">
                      <CardContent className="p-6 flex flex-col h-full">
                        <div className="w-10 h-10 rounded-md bg-accent text-accent-foreground flex items-center justify-center mb-4 shrink-0">
                          <IconComponent className="w-5 h-5 text-primary" />
                        </div>
                        <h3 className="font-default text-lg font-bold text-foreground mb-2">
                          {val.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed flex-grow">
                          {val.description}
                        </p>
                        <div className="mt-4 pt-3 border-t border-border flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-primary block" />
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            BMF Benchmark
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>;
            })}
            </div>

            {/* Leadership & Legacy Context Box */}
            <div className="p-6 md:p-8 rounded-lg bg-card border border-border shadow-xs">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <Award className="w-6 h-6 text-primary shrink-0" />
                  <div>
                    <h4 className="font-default text-base font-bold text-foreground">
                      20+ years of scaffolding leadership
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Pete Wood (Director) and the Adam Story Legacy
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold px-3 py-1 bg-accent text-accent-foreground rounded-md">
                  <span>NASC</span>
                  <span>•</span>
                  <span>CHAS</span>
                </div>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed mt-4">
                We believe dependable scaffolding is founded on mutual trust and engineering rigor. Whether supplying access for regional commercial developers, roofing specialists, solar installers, or residential homeowners, our team operates with open communication and uncompromising safety.
              </p>

              {/* Areas Covered & Sectors Served Lines */}
              <div className="mt-4 pt-4 border-t border-border space-y-2.5 text-xs sm:text-sm">
                <div className="flex items-start gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-foreground font-semibold">Areas covered:</strong> Berkshire, Surrey, Hampshire, Buckinghamshire, London and the wider South East
                  </span>
                </div>
                <div className="flex items-start gap-2 text-muted-foreground">
                  <Building2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-foreground font-semibold">Sectors we serve:</strong> Main contractors, developers, roofing contractors, solar installers, councils, property/facilities management and homeowners
                  </span>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3 sm:gap-4">
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/ContactRequestQuotePage">
                    Request a quote
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="border-border text-foreground hover:bg-accent">
                  <Link to="/HealthSafetyAccreditationsPage">
                    View safety accreditations
                  </Link>
                </Button>
                <Button asChild variant="outline" className="border-border text-foreground hover:bg-accent">
                  <Link to="/ProjectsCaseStudiesPage">
                    See our projects
                  </Link>
                </Button>
                <Button asChild variant="outline" className="border-border text-foreground hover:bg-accent">
                  <Link to="/TestimonialsPage">
                    Read client testimonials
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
export default function Values() {
  const wp_query_params = {
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  };
  const wp_query = new WP_Query(wp_query_params);
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <ValuesContent />
    </PostsProvider>;
}